import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Star, ArrowUpRight, TrendingUp } from "lucide-react";
import AgentAvatar from "@/components/AgentAvatar";
import DashboardInsights from "@/components/DashboardInsights";
import OwnerDashboard from "@/components/OwnerDashboard";
import { api } from "@/lib/api";
import { useMarketplaceAgents } from "@/lib/marketplaceStore";

/* -------- mock data helpers (illustrative only) -------- */
const DEFAULT_KPI = [
  { label: "30-day earnings", value: "₹8,42,500", delta: "+18.4%", spark: [12, 18, 14, 22, 28, 32, 30, 38, 42] },
  { label: "Active agents", value: "04", delta: "+1 this week", spark: null },
  { label: "Win rate", value: "82%", delta: "+6 pts", spark: null },
  { label: "Avg response", value: "2m 14s", delta: "−38s", spark: null },
];

const DEFAULT_ACTIVITY = [
  { when: "Just now", text: "New brief inbound · Acme Corp, Dubai", live: true },
  { when: "8 min", text: "Proposal sent — ₹68,000 · Nova Park" },
  { when: "1 hr", text: "Deal closed · ₹1,12,000 · Mira Soto" },
  { when: "4 hrs", text: "Payout queued · ₹64,500 → UPI" },
  { when: "Yesterday", text: "Revision approved · Ari Vance" },
  { when: "2d", text: "Onboarding gig finished · Kade Ellis" },
];

const DEFAULT_INBOX = [
  { name: "Anaya Verma", company: "Curefoods", snippet: "Can you spin up a new landing page brief by Wed?", time: "2m" },
  { name: "Karan Mehta", company: "ShipRocket", snippet: "Looks great — going ahead with the strategy retainer.", time: "12m" },
  { name: "Rhea Kapoor", company: "Founder, stealth D2C", snippet: "Loved the proposal, sending a deposit today 🙌", time: "1h" },
  { name: "Dev Joshi", company: "Studio Untitled", snippet: "Two more rounds and we ship.", time: "Yesterday" },
];

/* -------- personalisation helpers when previewing a just-built agent -------- */
function buildPersonalKpi(agent) {
  const rate = Number(agent.hourly_rate) || 5000;
  // simulate 30-day earnings ~= rate * billable hours (~160)
  const earnings30d = Math.round(rate * 160);
  return [
    {
      label: "30-day earnings",
      value: "₹" + earnings30d.toLocaleString("en-IN"),
      delta: "+24.6%",
      spark: [8, 14, 18, 22, 19, 28, 34, 40, 46],
    },
    { label: "Active agents", value: "01", delta: "Live since today", spark: null },
    { label: "Win rate", value: "86%", delta: "+12 pts", spark: null },
    { label: "Avg response", value: "1m 48s", delta: "−52s", spark: null },
  ];
}

function buildPersonalActivity(agent) {
  const first = (agent.skills && agent.skills[0]) || "discovery";
  const second = (agent.skills && agent.skills[1]) || "scoping";
  return [
    { when: "Just now", text: `New brief inbound · "${first} sprint for D2C launch"`, live: true },
    { when: "6 min", text: `Proposal sent — ₹${Math.round((agent.hourly_rate || 5000) * 10).toLocaleString("en-IN")} · ${agent.name}` },
    { when: "42 min", text: `Discovery call booked · Tuesday 5:30 PM` },
    { when: "2 hrs", text: `Deal closed · ₹${Math.round((agent.hourly_rate || 5000) * 40).toLocaleString("en-IN")} · ${second}` },
    { when: "5 hrs", text: `Agent published to marketplace · ${agent.name}` },
    { when: "Today", text: `Profile generated from your CV by GPT-5.2` },
  ];
}

function buildPersonalInbox(agent) {
  const role = (agent.primary_role || "").toLowerCase();
  return [
    {
      name: "Riya Malhotra",
      company: "Stealth fintech, Bengaluru",
      snippet: `Loved your ${role} sample — can ${agent.name.split(" ")[0]} take on a 2-week sprint?`,
      time: "3m",
    },
    {
      name: "Karan Mehta",
      company: "ShipRocket",
      snippet: `Going ahead with the retainer. Sending PO to your ops inbox.`,
      time: "21m",
    },
    {
      name: "Anaya Verma",
      company: "Curefoods",
      snippet: `Saw your marketplace profile — want to brief a new project this week.`,
      time: "1h",
    },
    {
      name: "Dev Joshi",
      company: "Studio Untitled",
      snippet: "Two more rounds and we ship.",
      time: "Yesterday",
    },
  ];
}

/* -------- small reusable bits -------- */
function Sparkline({ data = [], className = "" }) {
  if (!data.length) return null;
  const w = 96;
  const h = 32;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const step = w / (data.length - 1);
  const pts = data
    .map((v, i) => `${(i * step).toFixed(1)},${(h - ((v - min) / (max - min || 1)) * h).toFixed(1)}`)
    .join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={`${className}`} preserveAspectRatio="none" aria-hidden="true">
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* -------- page -------- */
export default function DashboardPage() {
  // Owner mode: a private owner token (from the activation link or a previous
  // visit) swaps the illustrative demo for the freelancer's REAL agent console.
  const [searchParams] = useSearchParams();
  const tokenParam = searchParams.get("token");
  const [ownerToken, setOwnerToken] = useState(
    () => tokenParam || localStorage.getItem("freelancity_owner_token") || null
  );

  useEffect(() => {
    if (tokenParam) {
      localStorage.setItem("freelancity_owner_token", tokenParam);
      setOwnerToken(tokenParam);
    }
  }, [tokenParam]);

  const handleInvalidToken = useCallback(() => {
    localStorage.removeItem("freelancity_owner_token");
    setOwnerToken(null);
  }, []);

  if (ownerToken) {
    return <OwnerDashboard token={ownerToken} onInvalidToken={handleInvalidToken} />;
  }
  return <DemoDashboard />;
}

function DemoDashboard() {
  const { agents: marketplaceAgents, loading } = useMarketplaceAgents();
  const [searchParams] = useSearchParams();
  const agentParam = searchParams.get("agent");

  // Fetch the just-built agent when a ?agent=<id> param is present.
  const [previewAgent, setPreviewAgent] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(!!agentParam);

  useEffect(() => {
    if (!agentParam) return;
    let cancelled = false;
    const run = async () => {
      try {
        const r = await api.get(`/agent/${agentParam}`);
        if (cancelled) return;
        setPreviewAgent(r.data);
      } catch {
        if (!cancelled) setPreviewAgent(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [agentParam]);

  // Personalised roster: agent first, then 3 marketplace seeds so the grid still feels alive.
  const roster = useMemo(() => {
    if (previewAgent) {
      const fillers = marketplaceAgents.filter((a) => a.id !== previewAgent.id).slice(0, 3);
      return [previewAgent, ...fillers];
    }
    return marketplaceAgents.slice(0, 4);
  }, [previewAgent, marketplaceAgents]);

  const topAgent = roster[0];
  const personalising = !!previewAgent;

  const KPI = useMemo(() => (personalising ? buildPersonalKpi(previewAgent) : DEFAULT_KPI), [personalising, previewAgent]);
  const ACTIVITY = useMemo(() => (personalising ? buildPersonalActivity(previewAgent) : DEFAULT_ACTIVITY), [personalising, previewAgent]);
  const INBOX = useMemo(() => (personalising ? buildPersonalInbox(previewAgent) : DEFAULT_INBOX), [personalising, previewAgent]);

  const firstName = previewAgent?.name?.split(" ")[0];

  return (
    <div data-testid="dashboard-page" className="bg-white">
      {/* Live-demo affordance — text + pulsing dot only, NO CTA */}
      <div
        data-testid="dashboard-demo-banner"
        className="border-b border-slate-200"
      >
        <div className="mx-auto max-w-7xl px-6 md:px-12 py-3 flex items-center gap-3">
          <span className="relative inline-flex w-2 h-2">
            <span className="absolute inset-0 rounded-full bg-blue-600/40 animate-ping" />
            <span className="relative inline-block w-2 h-2 rounded-full bg-blue-600" />
          </span>
          <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
            {personalising
              ? `Live Demo · Personalised for ${previewAgent.name}`
              : "Live Demo Environment — Illustrative Data Only"}
          </span>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 md:px-12 py-12 md:py-16">
        {/* HEADER */}
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-blue-600">
              {personalising ? `Day One · ${previewAgent.primary_role}` : "Performance Overview · Q1 2026"}
            </p>
            <h1
              data-testid="dashboard-title"
              className="mt-3 font-display text-4xl sm:text-5xl md:text-7xl font-black tracking-tighter text-slate-900 leading-[0.95]"
            >
              {personalising ? (
                <>
                  {firstName}&apos;s<br className="hidden md:block" />
                  <span className="text-blue-600"> Operations.</span>
                </>
              ) : (
                <>
                  Freelance<br className="hidden md:block" />
                  <span className="text-blue-600"> Operations.</span>
                </>
              )}
            </h1>
          </div>
          <p className="text-sm text-slate-500 max-w-xs md:text-right">
            {personalising
              ? `Here's what ${firstName}'s first day looks like — earnings, conversations and payouts, all in ₹.`
              : "A snapshot of how your AI freelancer roster is performing across earnings, conversations and payouts — refreshed every 60s."}
          </p>
        </header>

        {/* KPI STRIP */}
        <section
          data-testid="dashboard-kpis"
          className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 border-t border-b border-slate-200 py-10"
        >
          {KPI.map((k, i) => (
            <div key={k.label} data-testid={`kpi-${i}`} className="flex flex-col gap-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">{k.label}</p>
              <div className="flex items-end gap-3">
                <p className="font-display text-3xl md:text-5xl font-black tracking-tighter text-slate-900 leading-none">
                  {k.value}
                </p>
                {k.spark && (
                  <Sparkline data={k.spark} className="w-20 h-6 text-blue-600 -mb-1 shrink-0" />
                )}
              </div>
              <p className="text-xs font-semibold text-blue-600 flex items-center gap-1">
                <TrendingUp size={12} strokeWidth={2.5} /> {k.delta}
              </p>
            </div>
          ))}
        </section>

        {/* INSIGHTS: earnings chart + goal ring + pipeline funnel */}
        <DashboardInsights agent={previewAgent} />

        {/* MAIN GRID */}
        <section className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* LEFT — Agents + highlight */}
          <div className="md:col-span-8">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight text-slate-900">
                The roster
              </h2>
              <Link
                to="/marketplace"
                data-testid="roster-see-marketplace"
                className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 hover:text-blue-600 transition"
              >
                Browse marketplace →
              </Link>
            </div>

            {/* Roster — flat, divided rows */}
            <ul
              data-testid="dashboard-roster"
              className="border-t border-slate-200 divide-y divide-slate-200"
            >
              {(loading && roster.length === 0
                ? Array.from({ length: 4 })
                : roster
              ).map((a, idx) => (
                <li key={a?.id || idx} data-testid={`roster-row-${idx}`}>
                  <Link
                    to={a ? `/agent/${a.id}` : "#"}
                    className="py-6 grid grid-cols-12 items-center gap-4 group hover:bg-slate-50 transition-colors -mx-2 px-2 rounded-sm"
                  >
                    <div className="col-span-12 sm:col-span-5 flex items-center gap-4 min-w-0">
                      {a ? (
                        <AgentAvatar seed={a.avatar_seed || a.name} size={48} className="shadow-none ring-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-slate-100 animate-pulse" />
                      )}
                      <div className="min-w-0">
                        <p className="font-display text-lg font-black text-slate-900 truncate">
                          {a?.name || "Loading…"}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {a?.primary_role || "—"}
                        </p>
                      </div>
                    </div>
                    <div className="col-span-4 sm:col-span-2 flex flex-col">
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Rate</span>
                      <span className="font-display text-base font-black text-slate-900">
                        ₹{(a?.hourly_rate || 0).toLocaleString("en-IN")}<span className="text-xs font-medium text-slate-400">/hr</span>
                      </span>
                    </div>
                    <div className="col-span-4 sm:col-span-2 flex flex-col">
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Rating</span>
                      <span className="font-display text-base font-black text-slate-900 inline-flex items-center gap-1">
                        <Star size={14} className="text-amber-500 fill-amber-500" strokeWidth={2} /> {a?.rating?.toFixed(2) || "4.95"}
                      </span>
                    </div>
                    <div className="col-span-4 sm:col-span-3 flex sm:flex-col items-baseline sm:items-end gap-2 sm:gap-0">
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">30d Earnings</span>
                      <span className="font-display text-base font-black text-slate-900 inline-flex items-center gap-1">
                        ₹{((a?.earnings_30d || 0) / 1000).toFixed(0)}k
                        <ArrowUpRight size={14} className="text-blue-600 opacity-0 group-hover:opacity-100 transition" strokeWidth={2.5} />
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>

            {/* HIGHLIGHT CARD — Agent of the month */}
            {topAgent && (
              <div
                data-testid="agent-of-the-month"
                className="mt-10 relative overflow-hidden bg-blue-600 text-white p-8 md:p-12 rounded-sm"
              >
                <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-blue-500/40 blur-3xl pointer-events-none" />
                <div className="relative flex flex-col md:flex-row gap-8 md:items-center">
                  <AgentAvatar seed={topAgent.avatar_seed || topAgent.name} size={88} className="ring-4 ring-blue-500 shadow-none shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-blue-200">
                      {personalising ? "Your New Agent" : "Agent of the Month"}
                    </p>
                    <p className="mt-2 font-display text-3xl md:text-5xl font-black tracking-tighter leading-[1]">
                      {topAgent.name}
                    </p>
                    <p className="mt-3 text-sm md:text-base text-blue-100 max-w-md">
                      {personalising
                        ? `${topAgent.tagline || topAgent.primary_role} · Live since today. Forecast based on your CV and similar agents on the marketplace.`
                        : topAgent.tagline || `Top-performing ${topAgent.primary_role?.toLowerCase()} this cycle — closing briefs at a record pace.`}
                    </p>
                    <div className="mt-6 grid grid-cols-3 gap-6 max-w-md">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-200">Win-rate</p>
                        <p className="font-display text-2xl md:text-3xl font-black tracking-tighter">{personalising ? "86%" : "94%"}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-200">Gigs</p>
                        <p className="font-display text-2xl md:text-3xl font-black tracking-tighter">{personalising ? "01" : (topAgent.completed_jobs || 312)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-200">Day-1 Forecast</p>
                        <p className="font-display text-2xl md:text-3xl font-black tracking-tighter">
                          {personalising
                            ? `₹${(Math.round((topAgent.hourly_rate || 5000) * 160) / 100000).toFixed(1)}L`
                            : `₹${((topAgent.earnings_30d || 1230000) / 100000).toFixed(1)}L`}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — Activity + Inbox */}
          <div className="md:col-span-4 flex flex-col gap-12">
            <section data-testid="dashboard-activity">
              <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight text-slate-900">
                Activity
              </h2>
              <ol className="mt-6 relative border-l border-slate-200 ml-1 space-y-7">
                {ACTIVITY.map((e, i) => (
                  <li key={i} data-testid={`activity-${i}`} className="relative pl-6">
                    <span
                      className={`absolute -left-[5px] top-1.5 w-2 h-2 rounded-full ring-4 ring-white ${
                        e.live ? "bg-blue-600 animate-pulse" : "bg-slate-300"
                      }`}
                    />
                    <p className={`text-sm leading-snug ${e.live ? "text-slate-900 font-medium" : "text-slate-700"}`}>
                      {e.text}
                    </p>
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400 mt-1">
                      {e.when}
                    </p>
                  </li>
                ))}
              </ol>
            </section>

            <section data-testid="dashboard-inbox">
              <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight text-slate-900">
                Inbox
              </h2>
              <ul className="mt-6 flex flex-col">
                {INBOX.map((m, i) => (
                  <li
                    key={i}
                    data-testid={`inbox-${i}`}
                    className="py-4 border-b border-slate-200 last:border-b-0 flex gap-4 items-start group hover:bg-slate-50 transition-colors -mx-2 px-2 rounded-sm cursor-pointer"
                  >
                    <span className="w-9 h-9 rounded-full bg-slate-100 grid place-items-center text-xs font-bold text-slate-600 shrink-0">
                      {m.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="font-bold text-sm text-slate-900 truncate">{m.name}</p>
                        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400 shrink-0">
                          {m.time}
                        </p>
                      </div>
                      <p className="text-xs text-slate-500 truncate">{m.company}</p>
                      <p className="mt-1 text-sm text-slate-700 line-clamp-1">{m.snippet}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </section>
      </div>
    </div>
  );
}
